import { computed, Injectable, inject, Signal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree } from '@angular/forms/signals';
import { MceApiService } from '../../../core/mceapi.client.generated';
import { EngineeringNotation } from '../../../shared/functions/engineering-notation';
import { of } from 'rxjs';
import { createLoadDto, Load } from './load.model';
import { createPowerFactorDto } from '../common/power-factor/power-factor.model';
import { SystemVoltage, createSystemVoltageDto } from '../system-voltage/system-voltage.model';

function formatEngineeringValue(value: number | undefined, unit: string): string {
  return value === undefined ? '' : EngineeringNotation.toString(value, unit, 1);
}

@Injectable()  // no providedIn — component-scoped
export class LoadCalculationService {

  private readonly mceApiService = inject(MceApiService);

  private form!: Signal<FieldTree<Load>>;
  private systemVoltage!: Signal<FieldTree<SystemVoltage>>;

  /** Call once from the component constructor to wire up signals. */
  init(form: Signal<FieldTree<Load>>, systemVoltage: Signal<FieldTree<SystemVoltage>>) {
    this.form = form;
    this.systemVoltage = systemVoltage;
  }

  readonly loadTypesRx = rxResource({
    params: () => ({ systemVoltage: createSystemVoltageDto(this.systemVoltage()) }),
    stream: ({ params }) =>
      this.systemVoltage()().valid()
        ? this.mceApiService.v1LoadsLegacyLoads(params.systemVoltage())
        : of([])
  });
  readonly loadTypes = computed(() => this.loadTypesRx.value() ?? []);

  readonly powerDCRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
      load: createLoadDto(this.form())()
    }),
    stream: ({ params }) =>
      this.form().case().value() === 'dc' && this.form().ib().valid() && this.systemVoltage()().valid()
        ? this.mceApiService.v1LoadsPowerDc({ systemVoltage: params.systemVoltage, load: params.load })
        : of(undefined)
  });
  readonly powerDC = computed(() => formatEngineeringValue(this.powerDCRx.value()?.p, 'W'));

  readonly powerACRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
      load: createLoadDto(this.form())()
    }),
    stream: ({ params }) =>
      this.form().case().value() === 'ac' && this.form().ib().valid() && this.form().pf().valid() && this.systemVoltage()().valid()
        ? this.mceApiService.v1LoadsPowerAc({ systemVoltage: params.systemVoltage, load: params.load })
        : of(undefined)
  });
  readonly powerAC = computed(() => formatEngineeringValue(this.powerACRx.value()?.s?.real, 'W'));
  readonly kvaAC = computed(() => formatEngineeringValue(this.powerACRx.value()?.s?.magnitude, 'VA'));


   userWatts = signal<number | undefined>(undefined);
   ibFromWRx = rxResource({
     params: () => ({
       systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
       power: this.userWatts() ? { p: this.userWatts() } : undefined,
       efficiency: { point: this.form().efficiency().value() }
     }),
     stream: ({ params }) =>
       !params.power
         ? of(undefined)
         : (params.systemVoltage.case === 'dc')
           ? this.mceApiService.v1LoadsCurrentDc({
             systemVoltage: params.systemVoltage,
             power: params.power,
             efficiency: params.efficiency
           })
           : this.mceApiService.v1LoadsCurrentAcReal({
             systemVoltage: params.systemVoltage,
             power: params.power,
             pf: createPowerFactorDto(this.form().pf)(),// { pf: this.form().pf().value(), lagging: this.form().lagging().value() },
             efficiency: params.efficiency
           })
   });
   ibFromW = computed(() => this.ibFromWRx.value()?.i);

  userVA = signal<number | undefined>(undefined);
  ibFromVARx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.systemVoltage())(),
      apparentPower: this.userVA() ? { s: this.userVA() } : undefined,
      pf: createPowerFactorDto(this.form().pf)(),// { pf: this.form().pf().value(), lagging: this.form().lagging().value() },
      efficiency: { point: this.form().efficiency().value() }
    }),
    stream: ({ params }) =>
      !params.apparentPower
        ? of(undefined)
        : this.mceApiService.v1LoadsCurrentAcApparent({
          systemVoltage: params.systemVoltage,
          apparentPower: params.apparentPower,
          pf: params.pf,
          efficiency: params.efficiency
        })
  });
  ibFromVA = computed(() => this.ibFromVARx.value()?.i);

}
